// Guarantees of Origin (GO / Εγγυήσεις Προέλευσης):
// EUR/MWh green-certificate adder on eligible renewable energy.
// No market price series — the rate is a user assumption (DAPEEP GO auctions).

export const DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG = {
  enabled: false,
  eurPerMwh: 0,
  eligibleEnergyBasis: "net_export",
  revenueTreatment: "DISCLOSURE_ONLY",
  termYears: "", // empty = full project horizon
};

export const GUARANTEES_OF_ORIGIN_TREATMENT_OPTIONS = [
  {
    value: "DISCLOSURE_ONLY",
    label: "Disclosure only / upside case",
  },
  {
    value: "CONTRACTED_BASE_CASE",
    label: "Contracted — included in base case",
  },
];

export const GUARANTEES_OF_ORIGIN_BASIS_OPTIONS = [
  {
    value: "net_export",
    label: "Net grid export",
  },
  {
    value: "gross_generation",
    label: "Gross PV generation",
  },
];

export const GUARANTEES_OF_ORIGIN_HELPER_TEXT =
  "EUR/MWh green-certificate adder on eligible renewable energy. Net grid export (default) counts only metered grid injection; gross PV generation counts total PV production. Applied to the selected energy basis and tracks annual PV degradation. 'Contracted' adds it to base-case NPV/IRR/DSCR and is available for merchant arbitrage settlement only — under tolling / PPA the GO ownership follows the energy to the offtaker, so use disclosure only. Reference price from DAPEEP GO auctions.";

const VALID_TREATMENTS = new Set(
  GUARANTEES_OF_ORIGIN_TREATMENT_OPTIONS.map((option) => option.value)
);

const VALID_BASES = new Set(
  GUARANTEES_OF_ORIGIN_BASIS_OPTIONS.map((option) => option.value)
);

function parseFiniteNumber(value, fallback = 0) {
  if (value === "" || value == null) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function normalizeGuaranteesOfOriginConfig(source = {}) {
  const treatment = source?.revenueTreatment ?? source?.revenue_treatment;
  const basis = source?.eligibleEnergyBasis ?? source?.eligible_energy_basis;
  const termRaw = source?.termYears ?? source?.term_years;
  return {
    eurPerMwh: parseFiniteNumber(
      source?.eurPerMwh ?? source?.eur_per_mwh,
      DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG.eurPerMwh
    ),
    eligibleEnergyBasis: VALID_BASES.has(basis)
      ? basis
      : DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG.eligibleEnergyBasis,
    revenueTreatment: VALID_TREATMENTS.has(treatment)
      ? treatment
      : DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG.revenueTreatment,
    termYears:
      termRaw === "" || termRaw == null
        ? ""
        : Math.max(0, Math.floor(parseFiniteNumber(termRaw, 0))),
  };
}

export function buildGuaranteesOfOriginPayload(source = {}) {
  const normalized = normalizeGuaranteesOfOriginConfig(source);
  return {
    enabled: true,
    eurPerMwh: normalized.eurPerMwh,
    eligibleEnergyBasis: normalized.eligibleEnergyBasis,
    revenueTreatment: normalized.revenueTreatment,
    termYears:
      normalized.termYears === "" || normalized.termYears === 0
        ? null
        : normalized.termYears,
  };
}

export function validateGuaranteesOfOriginConfig({
  enabled,
  goConfig = {},
  settlementIsArbitrage = true,
} = {}) {
  if (!enabled) return { errors: [], warnings: [] };

  const errors = [];
  const warnings = [];
  const normalized = normalizeGuaranteesOfOriginConfig(goConfig);

  if (normalized.eurPerMwh < 0) {
    errors.push("Guarantees of Origin price must be greater than or equal to 0 EUR/MWh.");
  }
  if (
    normalized.revenueTreatment === "CONTRACTED_BASE_CASE" &&
    !settlementIsArbitrage
  ) {
    errors.push(
      "Contracted Guarantees of Origin are supported only for merchant arbitrage settlement. Use 'Disclosure only / upside case' for tolling / PPA structures."
    );
  }
  if (normalized.eurPerMwh === 0) {
    warnings.push(
      "Guarantees of Origin are selected but the price is zero, so Guarantees of Origin revenue will be zero."
    );
  }
  return { errors, warnings };
}
